import { useState, useEffect, useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import { Button, Card, Form, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { Download, FileEarmarkPdf, FileEarmarkSpreadsheet, FloppyFill, InfoCircle } from 'react-bootstrap-icons';
import { axiosClient } from '../shared/apiConfig';
import { toast } from 'react-toastify';
import { LocalDate } from '@js-joda/core';
import { getDynamicDateRange, QuickDateRangeDropdown } from '../QuickDateRangeDropdown';
import type { ServerTransferFacilityLite, ServerTransferSavedReportResponse } from '../shared/api/axios-client';
import type { QuickDateOption } from './BaseReport';
import {
    ReportFacilityMultiSelectRow,
    ReportFacilityOption,
    useReportFacilityMultiSelect,
} from './ReportFacilityMultiSelect';
import { SaveReportModal } from './SaveReportModal';
import { ManageSavedReportsModal } from './ManageSavedReportsModal';

interface CustomReportDefinition {
    customReportDefinitionId: number;
    name: string;
    description?: string | null;
    outputFormat: 'Pdf' | 'Excel';
    dataSource: string;
    filterType: 'DateRange' | 'CardList' | 'None';
}

interface Props {
    facilityId?: number;
    facilityName?: string;
    /** Operator-level: all facilities for multi-select (same pattern as revenue report). */
    facilities?: ServerTransferFacilityLite[];
    /** When set (operator view), keeps multi-select in sync with parent so actions are not blocked on empty selection. */
    facilityIds?: number[];
    /** When both are set, the saved-report list is owned by the parent (shared with Recurring tab). */
    savedReports?: ServerTransferSavedReportResponse[];
    setSavedReports?: Dispatch<SetStateAction<ServerTransferSavedReportResponse[]>>;
}

function dateInputSlice(s: string): string {
    return s.length >= 10 ? s.substring(0, 10) : s;
}

function normalizeRequestDateTime(value: string | null | undefined): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    // Backend DateTime binder rejects "YYYY-MM-DD HH:mm:ss"; use ISO-ish "T" separator.
    return trimmed.includes(' ') ? trimmed.replace(' ', 'T') : trimmed;
}

/** Matches `Data.Entities.Reports.ReportType.Custom` (after Payments, Subscriptions, Aging, ReedemedValidations). */
const REPORT_TYPE_CUSTOM = 4;

export function CustomReport({
    facilityId,
    facilityName,
    facilities: facilitiesLite,
    facilityIds,
    savedReports: savedReportsProp,
    setSavedReports: setSavedReportsProp,
}: Props) {
    const [definitions, setDefinitions] = useState<CustomReportDefinition[]>([]);
    const [internalSavedReports, setInternalSavedReports] = useState<ServerTransferSavedReportResponse[]>([]);
    const isControlledSaved =
        savedReportsProp !== undefined && setSavedReportsProp !== undefined;
    const savedReports = isControlledSaved ? savedReportsProp : internalSavedReports;
    const setSavedReports = isControlledSaved ? setSavedReportsProp : setInternalSavedReports;
    const [selectedId, setSelectedId] = useState<number | ''>('');
    const [from, setFrom] = useState<string>('');
    const [to, setTo] = useState<string>('');
    const [quickDateMode, setQuickDateMode] = useState<QuickDateOption | 'All'>('All');
    const [cardNumbers, setCardNumbers] = useState<string[]>([]);
    const [cardFileName, setCardFileName] = useState<string>('');
    const [saveReportName, setSaveReportName] = useState('');
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showManageModal, setShowManageModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingDefs, setLoadingDefs] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const facilities: ReportFacilityOption[] | undefined = useMemo(() => {
        if (!facilitiesLite?.length) return undefined;
        return facilitiesLite
            .filter((f) => f.facilityId != null)
            .map((f) => ({
                facilityId: f.facilityId!,
                name: f.name,
            }));
    }, [facilitiesLite]);

    const isMultiFacility = Boolean(facilities?.length);
    const {
        facilityOptions,
        selectedOptions,
        selectedIds,
        setSelectedIds,
        onMultiChange,
        selectAll,
        clearAll,
    } = useReportFacilityMultiSelect(
        facilities,
        isMultiFacility && facilityIds !== undefined ? facilityIds : undefined
    );

    const handleQuickDateModeChange = useCallback((mode: QuickDateOption | 'All') => {
        setQuickDateMode(mode);
        if (mode === 'All') {
            setFrom('');
            setTo('');
        } else if (mode !== 'Custom') {
            const range = getDynamicDateRange(mode);
            if (range) {
                setFrom(range.from);
                setTo(range.to);
            }
        }
    }, []);

    useEffect(() => {
        setLoadingDefs(true);
        const defsReq = axiosClient.get<CustomReportDefinition[]>('/monitor/custom-reports/definitions');

        if (isControlledSaved) {
            defsReq
                .then((definitionsRes) => setDefinitions(definitionsRes.data))
                .catch(() => setError('Failed to load custom report definitions.'))
                .finally(() => setLoadingDefs(false));
            return;
        }

        Promise.all([
            defsReq,
            axiosClient.get<ServerTransferSavedReportResponse[]>('/monitor/custom-reports/saved-reports', {
                params: { facilityId: isMultiFacility ? 0 : (facilityId ?? 0) },
            }),
        ])
            .then(([definitionsRes, savedReportsRes]) => {
                setDefinitions(definitionsRes.data);
                setSavedReports(savedReportsRes.data);
            })
            .catch(() => setError('Failed to load custom report definitions.'))
            .finally(() => setLoadingDefs(false));
    }, [facilityId, isMultiFacility, isControlledSaved]);

    const selectedDef = definitions.find((d) => d.customReportDefinitionId === selectedId);

    const filterType = selectedDef?.filterType ?? 'DateRange';

    const customDatesIncomplete =
        filterType === 'DateRange' && quickDateMode === 'Custom' && (!from || !to);

    const getEffectiveFromTo = useCallback((): { from: string | null; to: string | null } => {
        if (quickDateMode === 'All') return { from: null, to: null };
        if (quickDateMode === 'Custom') {
            return { from: from || null, to: to || null };
        }
        const range = getDynamicDateRange(quickDateMode);
        return range ? { from: range.from, to: range.to } : { from: null, to: null };
    }, [quickDateMode, from, to]);

    const canSaveReport = useMemo(() => {
        if (selectedId === '') return false;
        if (customDatesIncomplete) return false;
        if (isMultiFacility) return selectedIds.length > 0;
        return true;
    }, [selectedId, customDatesIncomplete, isMultiFacility, selectedIds]);

    const handleRequestEmail = useCallback(async () => {
        if (selectedId === '') return;

        setLoading(true);
        setError(null);

        const { from: effFrom, to: effTo } = getEffectiveFromTo();

        try {
            const baseBody = isMultiFacility
                ? {
                      facilityId: selectedIds.length === 1 ? selectedIds[0] : 0,
                      facilityIds: selectedIds.length > 1 ? selectedIds : null,
                  }
                : { facilityId: facilityId ?? 0 };

            const body = filterType === 'CardList'
                ? { ...baseBody, fileLines: cardNumbers }
                : {
                      ...baseBody,
                      from: normalizeRequestDateTime(effFrom),
                      to: normalizeRequestDateTime(effTo),
                  };

            await axiosClient.post(
                `/monitor/custom-reports/${selectedId}/request-email`,
                body
            );
            toast.success(
                'Report queued. Check your email shortly for a download link.'
            );
        } catch {
            setError('Could not queue the report. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [selectedId, facilityId, isMultiFacility, selectedIds, getEffectiveFromTo, filterType, cardNumbers]);

    const handleSaveReport = useCallback(async () => {
        const trimmedName = saveReportName.trim();
        if (!trimmedName) {
            toast.error('Please enter a name for the report.');
            return;
        }
        if (selectedId === '') {
            toast.error('Please select a report first.');
            return;
        }
        if (savedReports.some((r) => r.name === trimmedName)) {
            toast.error('A report with this name already exists.');
            return;
        }

        const selectedFacilityId = isMultiFacility
            ? (selectedIds.length === 1 ? selectedIds[0] : 0)
            : (facilityId ?? 0);

        const { from: effFrom, to: effTo } = getEffectiveFromTo();

        const filterForm: Record<string, unknown> = {
            email: '',
            name: '',
            from: effFrom ?? '',
            to: effTo ?? '',
            paymentType: '',
            paymentStatus: '',
            quickDateMode: quickDateMode,
            quickPayoutDateMode: 'All',
            age: '',
            customReportDefinitionId: selectedId,
            facilityId: selectedFacilityId,
        };
        if (isMultiFacility && selectedIds.length > 0) {
            filterForm.facilityIds = [...selectedIds];
        }

        const payload = {
            reportType: REPORT_TYPE_CUSTOM,
            request: {
                facilityId: selectedFacilityId,
                name: trimmedName,
                filterJson: JSON.stringify(filterForm),
            },
        };

        try {
            const response = await axiosClient.post<ServerTransferSavedReportResponse>(
                '/monitor/custom-reports/add-saved-report',
                payload
            );
            setSavedReports((prev) => [...prev, response.data]);
            toast.success('Report saved successfully.');
            setSaveReportName('');
            setShowSaveModal(false);
        } catch {
            toast.error('Failed to save report.');
        }
    }, [saveReportName, selectedId, savedReports, isMultiFacility, selectedIds, facilityId, getEffectiveFromTo, quickDateMode]);

    const handleSelectDefinition = useCallback((id: number) => {
        setSelectedId(id);
        setCardNumbers([]);
        setCardFileName('');
    }, []);

    const handleCardFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCardFileName(file.name);
        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target?.result as string;
            const lines = text
                .split(/\r?\n/)
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
            setCardNumbers(lines);
        };
        reader.readAsText(file);
    }, []);

    const groupedDefinitions = useMemo(() => {
        const map = new Map<string, CustomReportDefinition[]>();
        definitions.forEach((d) => {
            const existing = map.get(d.name) ?? [];
            map.set(d.name, [...existing, d]);
        });
        return Array.from(map.entries()).map(([name, defs]) => ({ name, defs }));
    }, [definitions]);

    if (loadingDefs) {
        return (
            <div className="d-flex justify-content-center align-items-center mt-5">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading reports…
            </div>
        );
    }

    if (definitions.length === 0) {
        return (
            <Alert variant="info" className="mt-3">
                No custom reports have been configured for your account.
            </Alert>
        );
    }

    return (
        <div className="mt-2">
            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

            {facilityName && !isMultiFacility && (
                <p className="text-muted mb-3">
                    Facility: <strong>{facilityName}</strong>
                </p>
            )}

            {isMultiFacility && (
                <ReportFacilityMultiSelectRow
                    facilityOptions={facilityOptions}
                    selectedOptions={selectedOptions}
                    onMultiChange={onMultiChange}
                    selectAll={selectAll}
                    clearAll={clearAll}
                />
            )}

            <div className="mt-3 d-flex flex-wrap gap-3">
                {groupedDefinitions.map(({ name, defs }) => {
                    const groupSelected = defs.some((d) => d.customReportDefinitionId === selectedId);
                    return (
                        <Card
                            key={name}
                            className={`custom-report-card ${groupSelected ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                            style={{ minWidth: 220, maxWidth: 300 }}
                        >
                            <Card.Body>
                                <div className="fw-semibold mb-1">{name}</div>
                                <div className="d-flex flex-wrap gap-2">
                                    {defs.map((d) => (
                                        <Badge
                                            key={d.customReportDefinitionId}
                                            bg={d.outputFormat === 'Pdf' ? 'danger' : 'success'}
                                            className={`d-flex align-items-center gap-1 px-2 py-2${d.customReportDefinitionId === selectedId ? ' opacity-100' : ' opacity-50'}`}
                                            style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                                            onClick={() => handleSelectDefinition(d.customReportDefinitionId)}
                                        >
                                            {d.outputFormat === 'Pdf'
                                                ? <FileEarmarkPdf />
                                                : <FileEarmarkSpreadsheet />}
                                            {d.outputFormat}
                                        </Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    );
                })}
            </div>

            {selectedDef?.description?.trim() && (
                <div
                    className="mt-3 border-start border-3 border-primary bg-light rounded-end py-3 px-3"
                    role="note"
                >
                    <div className="d-flex gap-2 align-items-start">
                        <InfoCircle className="text-primary flex-shrink-0 mt-1" aria-hidden />
                        <div
                            className="small text-body-secondary"
                            style={{ whiteSpace: 'pre-wrap' }}
                        >
                            {selectedDef.description.trim()}
                        </div>
                    </div>
                </div>
            )}

            {filterType === 'DateRange' && (
                <Row className="g-2 mt-2 mb-2 align-items-end">
                    <Col xs={12} md={3}>
                        <QuickDateRangeDropdown
                            mode={quickDateMode}
                            onModeChange={handleQuickDateModeChange}
                            label="Report period"
                            onChange={(range, mode) => {
                                setQuickDateMode(mode);
                                setFrom(range.from);
                                setTo(range.to);
                            }}
                        />
                    </Col>
                    {quickDateMode === 'Custom' && (
                        <>
                            <Col xs={12} md={3}>
                                <Form.Group>
                                    <Form.Label className="small form-label">From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="from"
                                        value={dateInputSlice(from)}
                                        onChange={(e) => setFrom(e.target.value)}
                                        size="sm"
                                        max={dateInputSlice(to) || LocalDate.now().toString()}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={3}>
                                <Form.Group>
                                    <Form.Label className="small form-label">To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="to"
                                        value={dateInputSlice(to)}
                                        onChange={(e) => setTo(e.target.value)}
                                        size="sm"
                                        min={dateInputSlice(from)}
                                        max={LocalDate.now().toString()}
                                    />
                                </Form.Group>
                            </Col>
                        </>
                    )}
                </Row>
            )}

            {filterType === 'CardList' && (
                <Row className="g-2 mt-2 mb-2 align-items-end">
                    <Col xs={12} md={5}>
                        <Form.Group>
                            <Form.Label className="small form-label">
                                Upload card list (.txt)
                            </Form.Label>
                            <Form.Control
                                type="file"
                                accept=".txt"
                                size="sm"
                                onChange={handleCardFileChange}
                            />
                        </Form.Group>
                        {cardNumbers.length > 0 && (
                            <div className="small text-muted mt-1">
                                {cardNumbers.length} card{cardNumbers.length !== 1 ? 's' : ''} loaded
                                {cardFileName ? ` from ${cardFileName}` : ''}
                            </div>
                        )}
                    </Col>
                </Row>
            )}

            {selectedId !== '' && (
                <Row className="g-2 mt-2 mb-2 align-items-end">
                    <Col md="auto">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleRequestEmail}
                            disabled={
                                loading ||
                                (isMultiFacility && selectedIds.length === 0) ||
                                (filterType === 'DateRange' && customDatesIncomplete) ||
                                (filterType === 'CardList' && cardNumbers.length === 0)
                            }
                        >
                            {loading ? (
                                <><Spinner animation="border" size="sm" className="me-2" />Sending…</>
                            ) : (
                                <><Download className="me-2 icon-shift-up" />Email report</>
                            )}
                        </Button>
                    </Col>
                    {filterType === 'DateRange' && (
                        <>
                            <Col md="auto">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setShowSaveModal(true)}
                                    disabled={!canSaveReport}
                                >
                                    <FloppyFill className="me-2 icon-shift-up" />
                                    Save Report Criteria
                                </Button>
                            </Col>
                            <Col md="auto">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setShowManageModal(true)}
                                >
                                    Saved Reports
                                </Button>
                            </Col>
                        </>
                    )}
                </Row>
            )}

            <SaveReportModal
                show={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                onSave={handleSaveReport}
                reportName={saveReportName}
                setReportName={setSaveReportName}
            />
            <ManageSavedReportsModal
                show={showManageModal}
                onClose={() => setShowManageModal(false)}
                savedReports={savedReports}
                setSavedReports={setSavedReports}
                facilities={facilities}
                customReportDefinitions={definitions.map((d) => ({
                    customReportDefinitionId: d.customReportDefinitionId,
                    name: d.name,
                }))}
                onRun={async (filters) => {
                    const customDefinitionId = Number(
                        filters.customReportDefinitionId ?? filters.CustomReportDefinitionId
                    );
                    if (!Number.isNaN(customDefinitionId) && customDefinitionId > 0) {
                        handleSelectDefinition(customDefinitionId);
                    } else {
                        toast.error('Saved report is missing custom report definition.');
                        return;
                    }

                    const modeRaw = filters.quickDateMode ?? filters.QuickDateMode;
                    const modeStr = typeof modeRaw === 'string' ? modeRaw : undefined;

                    if (modeStr === 'Custom') {
                        setQuickDateMode('Custom');
                        const fromDate = String(filters.from ?? filters.From ?? '').trim();
                        const toDate = String(filters.to ?? filters.To ?? '').trim();
                        setFrom(fromDate ? dateInputSlice(fromDate) : '');
                        setTo(toDate ? dateInputSlice(toDate) : '');
                    } else if (modeStr === 'All' || modeStr === '') {
                        handleQuickDateModeChange('All');
                    } else if (modeStr) {
                        handleQuickDateModeChange(modeStr as QuickDateOption | 'All');
                    } else {
                        const fromDate = String(filters.From ?? filters.from ?? '').trim();
                        const toDate = String(filters.To ?? filters.to ?? '').trim();
                        if (fromDate || toDate) {
                            setQuickDateMode('Custom');
                            setFrom(fromDate ? dateInputSlice(fromDate) : '');
                            setTo(toDate ? dateInputSlice(toDate) : '');
                        } else {
                            handleQuickDateModeChange('All');
                        }
                    }

                    const fids = filters.facilityIds ?? filters.FacilityIds;
                    if (isMultiFacility && Array.isArray(fids) && fids.length > 0) {
                        setSelectedIds(
                            fids.map((x: unknown) => Number(x)).filter((n) => !Number.isNaN(n))
                        );
                    } else if (isMultiFacility) {
                        const selectedFacilityId = Number(filters.facilityId ?? filters.FacilityId ?? 0);
                        setSelectedIds(selectedFacilityId > 0 ? [selectedFacilityId] : []);
                    }

                    let runFrom: string | null = null;
                    let runTo: string | null = null;
                    if (modeStr === 'Custom') {
                        runFrom = String(filters.from ?? filters.From ?? '').trim() || null;
                        runTo = String(filters.to ?? filters.To ?? '').trim() || null;
                    } else if (modeStr && modeStr !== 'All') {
                        const range = getDynamicDateRange(modeStr as QuickDateOption);
                        runFrom = range?.from ?? null;
                        runTo = range?.to ?? null;
                    }

                    const selectedIdsFromFilter = Array.isArray(fids)
                        ? fids.map((x: unknown) => Number(x)).filter((n) => !Number.isNaN(n))
                        : [];
                    const selectedFacilityId = Number(filters.facilityId ?? filters.FacilityId ?? 0);
                    const requestBody =
                        isMultiFacility
                            ? {
                                  facilityId:
                                      selectedIdsFromFilter.length === 1
                                          ? selectedIdsFromFilter[0]
                                          : selectedFacilityId > 0
                                              ? selectedFacilityId
                                              : 0,
                                  facilityIds:
                                      selectedIdsFromFilter.length > 1 ? selectedIdsFromFilter : null,
                                  from: normalizeRequestDateTime(runFrom),
                                  to: normalizeRequestDateTime(runTo),
                              }
                            : {
                                  facilityId: facilityId ?? 0,
                                  from: normalizeRequestDateTime(runFrom),
                                  to: normalizeRequestDateTime(runTo),
                              };

                    setLoading(true);
                    setError(null);
                    try {
                        await axiosClient.post(
                            `/monitor/custom-reports/${customDefinitionId}/request-email`,
                            requestBody
                        );
                        toast.success(
                            'Report queued. Check your email shortly for a download link.'
                        );
                    } catch {
                        setError('Could not queue the report. Please try again.');
                        throw new Error('Custom report request failed');
                    } finally {
                        setLoading(false);
                    }
                }}
                reportType="Custom Report"
                onDelete={async (savedReportId) => {
                    await axiosClient.delete(`/monitor/custom-reports/saved-report/${savedReportId}`);
                }}
            />
        </div>
    );
}
